import { atomWithKey as atom } from "./atomWithKey";
import { IPaymentState } from "../Types/Interface/IPaymentState";
import { enumOfferPassType } from "../Types/Enums/globalDefaultEnum";
import { PassengerTypeEnum } from "../Types/Enums/PassengerTypeEnum";
import { IPaymentCard } from "../Types/Interface/IPaymentCards";
export const paymentStateAtom = atom<IPaymentState>({
    key: "paymentStateAtom",
    default: {
        method: null,
        total: 0,
    },
});

interface RefundSummary {
    canceledTripCount: number;
    canceledTripTotal: number;
}

interface LegPaymentSummary {
    /** The direction's route endpoints, as raw stop refs — the heading the
        summary prints. It spans the whole journey, so on a Train+Shuttle trip it
        starts at the SHUTTLE's stop, and under multi-booking it comes from
        whichever trip was iterated first. It is a heading, not a station list;
        use `stationRefs` for that. */
    fromName: string;
    toName: string;
    /** Every TRAIN station in this direction, unioned across its trips. The
        shuttle rows name whichever of their own ends is NOT one of these. */
    stationRefs?: string[];
    priceClass: string;
    productName?: string;
    passengerDetails: {
        successPassengers?: {
            amount: number;
            description: string;
        }[];
        shuttlePassengers?: {
            description: string,
            /** Riders on this row. Carried explicitly so neither the group-by-stop
                merge nor the renderer has to parse it back out of `description`. */
            count?: number,
            amount: number,
            fromName?: string,
            toName?: string,
            /** Paid for, then refunded. Rendered struck through + badged, and
                excluded from the direction total — as a refunded ancillary is. */
            isRefunded?: boolean
        }[],
        appliedPass: {
            passType: enumOfferPassType
            passengerType: PassengerTypeEnum;
            amount: number;
        }[],
        refundPassengers?: [],
        exchangeSuccessPassengers?: [],
        exchangeFee?: string;
    },
    outboundSuccessTotalPrice: number,
    outboundTotalPrice: number,
    outboundRefundedTotalPrice: number,
    tripCount?: number;
    tripTotal?: number;
    refundSummary?: RefundSummary;
}

export interface ICheckoutPaymentSummary {
    outbound?: LegPaymentSummary;
    inbound?: LegPaymentSummary;
    payment: {
        total: number;
        refundedAmount: number;
        refundedTotal: number;
        subTotal: number;
        vatPercentage: any;
        currency?: string;
        alreadyPaid?: number;
    },
    otherDetails?: {
        addons?: {
            outbound: {
                amount: number;
                description: string
            }[],
            inbound?: {
                amount: number;
                description: string
            }[],
        }
        seatFees?: {
            outbound: {
                amount: number;
                description: string
            }[],
            inbound?: {
                amount: number;
                description: string
            }[],
        }
        isRefund?: boolean;
        // Named campaign/promo discounts (e.g. "Black Friday", promo code),
        // one line per passenger type. Set by applyNormalFlowDiscounts on the
        // fare-paying checkout surfaces. Rendered as the "Discounts" section.
        discounts?: {
            name: string;
            amount: number;
            code?: string;
        }[],
        // Already-paid fare credited on upgrade/change-date (the net of the
        // fares being exchanged away). Set by applyNormalFlowDiscounts; rendered
        // as the "Initial Fare" deduction above the gross ticket rows.
        initialFare?: number,
        voucher?: {
            amount: number;
        },
        offerPass?: {
            amount: number;
            description: string;
        },
        bookingFee?: {
            description?: string;
            amount?: number
        }
    } | null;
}

export const checkoutPaymentSummaryAtom = atom<ICheckoutPaymentSummary | {}>({
    key: "checkoutPaymentSummaryAtom",
    default: {}
})

export const paymentSuccessModalAtom = atom<boolean>({
    key: "paymentSuccessModalAtom",
    default: false,
});

export const carLicensePlateAtom = atom<string>({
    key: "carLicensePlateAtom",
    default: "",
});

export const saveCardAtom = atom<boolean>({
    key: "saveCardAtom",
    default: false,
});

export const termsAgreedAtom = atom<boolean>({
    key: "termsAgreedAtom",
    default: false,
});

export const paymentSubmittingAtom = atom<boolean>({
    key: "paymentSubmittingAtom",
    default: false,
});

export const paymentCardsAtom = atom<IPaymentCard[]>({
    key: "paymentCardsAtom",
    default: [],
})